import React, { useState, useRef, useEffect } from "react";
import "./Topbar.css";
import { FiSettings, FiUser, FiKey } from "react-icons/fi";
import useAuth from '../../hooks/useAuth';
import { useSeason } from '../../context/SeasonContext';
import axios from "axios";
import Card from '../ui/Card';
import Button from '../ui/Button';
import FormField from '../ui/FormField';

/**
 * Topbar – top navigation bar
 * @param {object} user     - logged-in user object
 * @param {fn}     onLogout - logout handler
 */
export default function Topbar({ section, page, onToggle, user, onLogout }) {
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const { changePassword } = useAuth();
  const { seasons, selectedSeason, changeSeason } = useSeason() || {};

  const [organisersList, setOrganisersList] = useState([]);
  const [selectedOrganiser, setSelectedOrganiser] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportType, setExportType] = useState("csv");

  useEffect(() => {
    const fetchOrganisers = async () => {
      if (!selectedSeason) return;
      try {
        const token = sessionStorage.getItem("maaze_token");
        const response = await axios.get(
          `https://maize.norissolutions.in/Php2/public/api/organisers?season_id=${selectedSeason}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const resData = response.data;
        let fetched = [];
        if (Array.isArray(resData?.data)) {
          fetched = resData.data;
        } else if (Array.isArray(resData?.data?.data)) {
          fetched = resData.data.data;
        } else if (Array.isArray(resData)) {
          fetched = resData;
        }

        if (user?.role === "Organiser") {
          fetched = fetched.filter(org => org.email === user.email);
        }
        setOrganisersList(fetched);
        if (fetched.length > 0) {
          setSelectedOrganiser(fetched[0].organiser_id);
        } else {
          setSelectedOrganiser("");
        }
      } catch (err) {
        console.log("Error fetching organisers:", err);
      }
    };
    if (showExportModal) {
      fetchOrganisers();
    }
  }, [selectedSeason, user, showExportModal]);

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    current: "",
    new: "",
  });
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleExport = async () => {
    if (!selectedOrganiser || !selectedSeason) {
      alert("Organiser or Season missing for export.");
      return;
    }
    setExportLoading(true);
    try {
      const token = sessionStorage.getItem("maaze_token");
      const url = `https://maize.norissolutions.in/Php/public/api/organisers/${selectedOrganiser}/export-report?season_id=${selectedSeason}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data?.data;
      if (data) {
        if (exportType === "csv") {
          const headers = Object.keys(data).join(',');
          const values = Object.values(data).map(v => `"${v}"`).join(',');
          const csvContent = `${headers}\n${values}`;

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement("a");
          const urlObj = URL.createObjectURL(blob);
          link.setAttribute("href", urlObj);
          link.setAttribute("download", `organiser_report_${selectedOrganiser}_season_${selectedSeason}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (exportType === "pdf") {
          const newWindow = window.open('', '', 'width=800,height=600');
          if (newWindow) {
            newWindow.document.write('<html><head><title>Organiser Report</title></head><body style="font-family: Arial, sans-serif;">');
            newWindow.document.write('<h2 style="text-align: center;">Organiser Report</h2>');
            newWindow.document.write('<table style="border-collapse: collapse; width: 100%; margin-top: 20px;">');
            newWindow.document.write('<thead style="background-color: #f8f9fa;"><tr>');
            Object.keys(data).forEach(key => {
              newWindow.document.write(`<th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">${key}</th>`);
            });
            newWindow.document.write('</tr></thead><tbody><tr>');
            Object.values(data).forEach(value => {
              newWindow.document.write(`<td style="padding: 12px; border: 1px solid #dee2e6;">${value !== null ? value : ''}</td>`);
            });
            newWindow.document.write('</tr></tbody></table>');
            newWindow.document.write('</body></html>');
            newWindow.document.close();
            newWindow.focus();
            setTimeout(() => {
              newWindow.print();
              newWindow.close();
            }, 250);
          } else {
            alert("Popup blocked! Please allow popups to generate PDF.");
          }
        }
      } else {
        alert("No data available for export.");
      }
    } catch (err) {
      console.error("Export Error:", err);
      alert(err.response?.data?.message || "Export failed. Please try again.");
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  return (
    <header className="topbar">
      <button
        className="topbar__toggle"
        onClick={onToggle}
        aria-label="Toggle sidebar"
      >
        ☰
      </button>

      <div className="topbar__breadcrumb">
        <span className="topbar__module">{section}</span>
        <span className="topbar__sep">›</span>
        <span className="topbar__page">{page}</span>
      </div>

      <div className="topbar__actions">
        <button className="topbar__btn" onClick={() => setShowExportModal(true)}>
          Export
        </button>

        <div className="topbar__btn topbar__season" style={{ background: 'var(--surface-50)' }}>
          {seasons && seasons.length > 0 ? (
            <select
              value={selectedSeason}
              onChange={(e) => changeSeason(e.target.value)}
              style={{ width: '100%', textAlign: 'center', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              {seasons.map(s => (
                <option key={s.season_id} value={s.season_id}>{s.season_name}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Please add season First.</span>
          )}
        </div>

        {/* User avatar + dropdown */}
        {user && (
          <div className="topbar__user-wrap" ref={dropRef}>
            <button
              className="topbar__user-btn"
              onClick={() => setDropOpen((o) => !o)}
            >
              <div className="topbar__user-avatar">{user.avatar}</div>
              <div className="topbar__user-info">
                <span className="topbar__user-name">{user.name}</span>
                <span className="topbar__user-role">{user.role}</span>
              </div>
              <span className="topbar__user-caret">{dropOpen ? "▲" : "▼"}</span>
            </button>

            {dropOpen && (
              <div className="topbar__dropdown">
                <div className="topbar__dropdown-header">
                  <div className="topbar__dropdown-avatar">{user.avatar}</div>
                  <div>
                    <div className="topbar__dropdown-name">{user.name}</div>
                    <div className="topbar__dropdown-email">{user.email}</div>
                  </div>
                </div>
                <div className="topbar__dropdown-divider" />
                <button className="topbar__dropdown-item">
                  <FiSettings /> Account Settings
                </button>

                <button className="topbar__dropdown-item">
                  <FiUser /> View Profile
                </button>

                <button
                  className="topbar__dropdown-item"
                  onClick={() => {
                    setDropOpen(false);
                    setShowPwdModal(true);
                  }}
                >
                  <FiKey /> Change Password
                </button>
                <div className="topbar__dropdown-divider" />
                <button
                  className="topbar__dropdown-item topbar__dropdown-item--danger"
                  onClick={() => {
                    setDropOpen(false);
                    onLogout();
                  }}
                >
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {showPwdModal && (
        <div className="pwd-modal-overlay">
          <div style={{ width: 340, maxWidth: '90%' }}>
            <Card title="Change Password">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <FormField
                  type="password"
                  placeholder="Current Password"
                  value={pwdForm.current}
                  onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                />
                <FormField
                  type="password"
                  placeholder="New Password"
                  value={pwdForm.new}
                  onChange={(e) => setPwdForm({ ...pwdForm, new: e.target.value })}
                />
              </div>

              {pwdMsg && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>{pwdMsg}</div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <Button variant="outline" onClick={() => setShowPwdModal(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={pwdLoading}
                  onClick={async () => {
                    setPwdLoading(true);
                    setPwdMsg("");

                    const res = await changePassword(
                      pwdForm.current,
                      pwdForm.new
                    );

                    setPwdLoading(false);
                    setPwdMsg(res.message);

                    if (res.success) {
                      setTimeout(() => {
                        setShowPwdModal(false);
                        setPwdForm({ current: "", new: "" });
                      }, 1200);
                    }
                  }}
                >
                  {pwdLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      {showExportModal && (
        <div className="pwd-modal-overlay">
          <div style={{ width: 340, maxWidth: '90%' }}>
            <Card title="Export Organiser Report">
              {organisersList && organisersList.length > 0 ? (
                <FormField
                  label="Select Organiser"
                  type="select"
                  options={organisersList.map(org => ({ label: org.organiser_name, value: org.organiser_id }))}
                  value={selectedOrganiser}
                  onChange={(e) => setSelectedOrganiser(e.target.value)}
                />
              ) : (
                <p style={{ color: "var(--text-muted)", marginBottom: "15px", fontSize: "14px" }}>
                  No Organisers available for this season.
                </p>
              )}

              <div style={{ marginTop: '15px' }}>
                <FormField
                  label="Format"
                  type="select"
                  options={[
                    { label: "CSV", value: "csv" },
                    { label: "PDF", value: "pdf" }
                  ]}
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <Button variant="outline" onClick={() => setShowExportModal(false)}>Cancel</Button>
                <Button
                  disabled={exportLoading || !selectedOrganiser}
                  onClick={handleExport}
                >
                  {exportLoading ? "Exporting..." : "Download"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </header>
  );
}
